import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dropdown } from '@/components/ui/select'
import { Modal } from '@/components/ui/modal'
import { apiClient } from '@/lib/api-client'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { toast } from 'react-hot-toast'

interface DeviceRequestModalProps {
  isOpen: boolean
  onClose: () => void
}

const kinds = [
  { value: 'new', title: 'New', text: 'I need something I do not have yet' },
  { value: 'replace', title: 'Replace', text: 'Swap a device I already hold' },
  { value: 'damage', title: 'Damaged', text: 'My current device is damaged' },
]

export function DeviceRequestModal({ isOpen, onClose }: DeviceRequestModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [deviceName, setDeviceName] = useState('')
  const [requestType, setRequestType] = useState('new')
  const [currentDeviceId, setCurrentDeviceId] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setDeviceName('')
    setRequestType('new')
    setCurrentDeviceId('')
    setReason('')
  }, [isOpen])

  const { data: holdings = [] } = useQuery({
    queryKey: ['my-holdings', user?.id],
    enabled: isOpen && !!user?.id,
    queryFn: async () => {
      const response = await apiClient.get('/assignments', {
        params: { employeeId: user?.id, status: 'active' },
      })
      return (response.data || []).filter((assignment: any) => (
        assignment.device && (assignment.status === 'approved' || assignment.status === 'pending')
      ))
    },
  })

  const needsDevice = requestType === 'replace' || requestType === 'damage'
  const reasonLabel = requestType === 'damage'
    ? 'What happened?'
    : requestType === 'replace'
      ? 'Why does it need replacing?'
      : 'Why do you need it?'
  const reasonPlaceholder = requestType === 'damage'
    ? 'The left button sticks and the battery no longer holds a charge.'
    : requestType === 'replace'
      ? 'The current one is too slow for the work I do now.'
      : 'I need this for daily work and do not have one assigned.'

  const submit = async () => {
    if (deviceName.trim().length < 2) {
      toast.error('Name the device you need')
      return
    }
    if (needsDevice && !currentDeviceId) {
      toast.error('Choose the device this request is about')
      return
    }
    if (reason.trim().length < 10) {
      toast.error('Add a little more detail, at least 10 characters')
      return
    }

    setLoading(true)
    try {
      await apiClient.post('/device-requests', {
        deviceName: deviceName.trim(),
        requestType,
        reason: reason.trim(),
        currentDeviceId: needsDevice ? Number(currentDeviceId) : undefined,
      })
      queryClient.invalidateQueries({ queryKey: ['device-requests'] })
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      toast.success(requestType === 'damage' ? 'Damage reported and request sent' : 'Request sent')
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not send the request')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request a device" size="lg">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium">What do you need?</label>
          <Input
            value={deviceName}
            onChange={(event) => setDeviceName(event.target.value)}
            placeholder="Bluetooth mouse"
            maxLength={120}
          />
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium">What kind of request is this?</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {kinds.map((kind) => {
              const selected = requestType === kind.value
              return (
                <button
                  key={kind.value}
                  type="button"
                  onClick={() => {
                    setRequestType(kind.value)
                    if (kind.value === 'new') setCurrentDeviceId('')
                  }}
                  className={cn(
                    'rounded-lg border px-3 py-3 text-left transition-colors',
                    selected ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted',
                  )}
                >
                  <span className="block text-sm font-medium">{kind.title}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{kind.text}</span>
                </button>
              )
            })}
          </div>
        </div>

        {needsDevice && (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium">
              {requestType === 'damage' ? 'Which device is damaged?' : 'Which device should be replaced?'}
            </label>
            {holdings.length === 0 ? (
              <p className="rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
                You do not have a device to choose. Request a new one instead.
              </p>
            ) : (
              <Dropdown
                value={currentDeviceId}
                placeholder="Select one of your devices"
                onChange={setCurrentDeviceId}
                options={holdings.map((assignment: any) => ({
                  value: String(assignment.device.id),
                  label: assignment.device.serialNumber
                    ? `${assignment.device.deviceModel?.name || assignment.device.name} · ${assignment.device.serialNumber}`
                    : (assignment.device.name || 'Device'),
                }))}
              />
            )}
          </div>
        )}

        <Textarea
          label={reasonLabel}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={reasonPlaceholder}
          rows={4}
          maxLength={1000}
        />

        {requestType === 'damage' && (
          <p className="rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
            This records the damage on that asset and asks for a replacement. The asset stays with you until an admin takes it back.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={loading}>
            {loading ? 'Sending...' : 'Send request'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
